import { useState } from 'react';
import {
  useDocuments,
  useDeleteDocument,
  useDocumentVersions,
  useUploadDocumentVersion,
  useUploadDocument,
  type DocumentType,
  type DocumentRow,
} from '../api/queries/documents';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { fmtDateTime } from '../utils/date';
import { LookupSelect } from '../components/LookupSelect';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { useLookupLabel } from '../api/queries/lookups';
import './DocumentsPage.css';

type DocSortKey = 'filename' | 'type' | 'project' | 'size' | 'created' | 'by';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentsPage(): JSX.Element {
  const [typeFilter, setTypeFilter] = useState<DocumentType | ''>('');
  const typeLabel = useLookupLabel('document_type');
  const { data, isPending, error } = useDocuments(
    typeFilter ? { document_type: typeFilter } : {},
  );
  const del = useDeleteDocument();
  const upload = useUploadDocument();
  const canWriteDocs = can('document' + '.write');
  const canDeleteDocs = can('document' + '.delete');
  const [actionError, setActionError] = useState<string | null>(null);
  const [versionsForId, setVersionsForId] = useState<number | null>(null);
  const [previewDoc, setPreviewDoc] = useState<DocumentRow | null>(null);

  const onPickUpload = async (file: File) => {
    if (file.size > 50 * 1024 * 1024) {
      setActionError('Files must be under 50 MB.');
      return;
    }
    setActionError(null);
    try {
      await upload.mutateAsync({
        file,
        document_type: typeFilter || 'other',
      });
    } catch (err) {
      setActionError(err instanceof ApiError ? `Upload failed (${err.status})` : 'Upload failed.');
    }
  };

  const onDelete = (doc: DocumentRow) => {
    if (!confirm(`Delete "${doc.original_filename}"? The file is removed from the vault.`)) return;
    const reason = prompt(
      'Reason for deletion (recorded in the audit log):',
      '',
    );
    // User cancelled the reason prompt → abort delete entirely.
    if (reason === null) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      if (!confirm('No reason given — proceed without one? An audit entry will still be written.')) {
        return;
      }
    }
    del.mutate({ id: doc.id, reason: trimmed.length > 0 ? trimmed : undefined }, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 403) {
          setActionError("You don't have permission to delete documents.");
          return;
        }
        setActionError('Could not delete document.');
      },
    });
  };

  const docs = data?.documents ?? [];
  const sort = useClientSort(docs, (d, key: DocSortKey) => {
    switch (key) {
      case 'filename': return d.original_filename;
      case 'type': return typeLabel(d.document_type);
      case 'project': return d.project_case_number ?? d.client_name ?? '';
      case 'size': return d.file_size;
      case 'created': return d.created_at;
      case 'by': return d.created_by_name ?? '';
      default: return '';
    }
  }, { key: 'created', dir: 'desc' });

  if (isPending) return <p className="dp-status">Loading documents…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view documents."
        : 'Failed to load documents.';
    return <p className="dp-status dp-error">{msg}</p>;
  }

  const totalSize = docs.reduce((sum, d) => sum + d.file_size, 0);

  return (
    <section className="page-stack page-stack-wide">
      <div className="card dp-toolbar">
      <header className="dp-header">
        <h2>Documents</h2>
        <div className="dp-header-controls">
          <LookupSelect
            category="document_type"
            value={typeFilter}
            onChange={setTypeFilter}
            className="form-input dp-filter"
            allowEmpty
            emptyLabel="All types"
          />
          {canWriteDocs && (
            <label className="btn-cyan-primary">
              {upload.isPending ? 'Uploading…' : 'Upload'}
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void onPickUpload(f);
                }}
                disabled={upload.isPending}
              />
            </label>
          )}
        </div>
      </header>
      </div>

      <div className="card">
        <div className="dp-summary">
          <div className="dp-summary-card">
            <div className="dp-summary-label">Tracked</div>
            <div className="dp-summary-value">{docs.length}</div>
          </div>
          <div className="dp-summary-card">
            <div className="dp-summary-label">Vault size</div>
            <div className="dp-summary-value">{fmtBytes(totalSize)}</div>
          </div>
        </div>
      </div>

      {actionError && (
        <div className="card">
          <p className="dp-error-banner" role="alert">{actionError}</p>
        </div>
      )}

      {docs.length === 0 ? (
        <div className="card">
          <div className="dp-empty">
            <p>No documents tracked yet.</p>
            <p className="dp-empty-meta">
              Documents land here when invoices/chain-of-custody/legal PDFs are
              generated, when emails are ingested, or via direct upload to{' '}
              <code>POST /api/documents</code>.
            </p>
          </div>
        </div>
      ) : (
        <div className="card card-tight dp-table-card table-scroll-card">
        <TableScroll id="documents">
        <table className="dp-table">
          <thead>
            <tr>
              <SortableTh column="filename" label="Filename" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="type" label="Type" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="project" label="Project / Client" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="size" label="Size" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="created" label="Created" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="by" label="By" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((d) => (
              <tr key={d.id} className={d.is_confidential ? 'dp-row-confidential' : ''}>
                <td className="dp-cell-name">
                  <button
                    type="button"
                    className="dp-link"
                    onClick={() => setPreviewDoc(d)}
                    title="Preview"
                  >
                    {d.original_filename}
                  </button>
                  {d.is_confidential && (
                    <span className="dp-pill dp-pill-warn">Confidential</span>
                  )}
                </td>
                <td>{typeLabel(d.document_type)}</td>
                <td>
                  {d.project_case_number ? <code>{d.project_case_number}</code> : null}
                  {d.client_name ? <span className="dp-client">{d.client_name}</span> : null}
                  {!d.project_case_number && !d.client_name && '—'}
                </td>
                <td>{fmtBytes(d.file_size)}</td>
                <td>{fmtDateTime(d.created_at)}</td>
                <td>{d.created_by_name ?? '—'}</td>
                <td className="dp-actions">
                  <button
                    type="button"
                    className="dp-link"
                    onClick={() => setVersionsForId(d.id)}
                    title="View version history"
                  >
                    versions{d.version_number > 1 ? ` (v${d.version_number})` : ''}
                  </button>
                  {canDeleteDocs && (
                    <button
                      type="button"
                      className="dp-row-delete"
                      onClick={() => onDelete(d)}
                      disabled={del.isPending}
                      aria-label="Delete"
                      title="Delete"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
              </TableScroll>
        </div>
      )}

      {versionsForId !== null && (
        <VersionsModal
          docId={versionsForId}
          canWrite={canWriteDocs}
          onClose={() => setVersionsForId(null)}
          onPreview={setPreviewDoc}
        />
      )}

      {previewDoc && (
        <DocumentPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </section>
  );
}

function VersionsModal({
  docId,
  canWrite,
  onClose,
  onPreview,
}: {
  docId: number;
  canWrite: boolean;
  onClose: () => void;
  onPreview: (doc: DocumentRow) => void;
}): JSX.Element {
  const versions = useDocumentVersions(docId);
  const newVersion = useUploadDocumentVersion();
  const [error, setError] = useState<string | null>(null);

  const onPickNewVersion = async (file: File) => {
    if (file.size > 50 * 1024 * 1024) {
      setError('Files must be under 50 MB.');
      return;
    }
    setError(null);
    try {
      await newVersion.mutateAsync({ parent_id: docId, file });
      void versions.refetch();
    } catch (err) {
      setError(err instanceof ApiError ? `Upload failed (${err.status})` : 'Upload failed.');
    }
  };

  const rows = versions.data?.versions ?? [];
  type VerSortKey = 'version' | 'filename' | 'size' | 'uploaded' | 'by';
  const verSort = useClientSort(rows, (v, key: VerSortKey) => {
    switch (key) {
      case 'version': return v.version_number;
      case 'filename': return v.original_filename;
      case 'size': return v.file_size;
      case 'uploaded': return v.created_at;
      case 'by': return v.created_by_name ?? '';
      default: return '';
    }
  }, { key: 'version', dir: 'desc' });

  return (
    <div className="dp-modal-backdrop" onClick={onClose}>
      <div className="dp-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dp-modal-header">
          <h3>Version History</h3>
          <button type="button" className="btn-text" onClick={onClose}>Close</button>
        </header>
        {versions.isPending ? (
          <p className="dp-status">Loading…</p>
        ) : versions.error ? (
          <p className="dp-error">Could not load versions.</p>
        ) : rows.length === 0 ? (
          <p className="dp-status">No versions on file.</p>
        ) : (
          <TableScroll id="documents-versions">
          <table className="dp-table">
            <thead>
              <tr>
                <SortableTh column="version" label="v#" sortKey={verSort.sortKey} sortDir={verSort.sortDir} onSort={verSort.toggle} />
                <SortableTh column="filename" label="Filename" sortKey={verSort.sortKey} sortDir={verSort.sortDir} onSort={verSort.toggle} />
                <SortableTh column="size" label="Size" sortKey={verSort.sortKey} sortDir={verSort.sortDir} onSort={verSort.toggle} />
                <SortableTh column="uploaded" label="Uploaded" sortKey={verSort.sortKey} sortDir={verSort.sortDir} onSort={verSort.toggle} />
                <SortableTh column="by" label="By" sortKey={verSort.sortKey} sortDir={verSort.sortDir} onSort={verSort.toggle} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {verSort.sorted.map((v) => (
                <tr key={v.id}>
                  <td>v{v.version_number}</td>
                  <td>{v.original_filename}</td>
                  <td>{fmtBytes(v.file_size)}</td>
                  <td>{fmtDateTime(v.created_at)}</td>
                  <td>{v.created_by_name ?? '—'}</td>
                  <td>
                    <button type="button" className="dp-link" onClick={() => onPreview(v)}>
                      preview
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
              </TableScroll>
        )}
        {canWrite && (
          <div className="dp-modal-actions">
            <label className="btn-cyan-primary">
              {newVersion.isPending ? 'Uploading…' : 'Upload new version'}
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void onPickNewVersion(f);
                }}
                disabled={newVersion.isPending}
              />
            </label>
            {error && <span className="field-error">{error}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
